import { forwardRef, ReactNode } from 'react';

import { Box, BoxProps } from '@mui/material';
import clsx from 'clsx';

import { tokens } from '../../../theme/generated/tokens';
import { CssVarDefinitions } from '../../../theme/types';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';
import { Inset, InsetProps } from '../../containers/Inset';
import { toDsVarsDefinition, mergeStyles, layoutVars } from '../../variables';

const classNamesFactory = classNamesFor('Page');

const classNames = {
  root: classNamesFactory.root(),
  content: classNamesFactory.element('content'),
};

const dsVars = toDsVarsDefinition({
  inset: {
    pr: '24px',
    pl: '24px',
  },
  divider: {
    color: tokens.theme.border.structural.neutral,
  },
  actionsGroup: {
    gap: '12px',
  },
});

export type PageProps = {
  /**
   * If set to true, the content of the page will be stacked, and spacing automatically applied
   */
  stack?: boolean;
  children: ReactNode;
  cssVars?: CssVarDefinitions;
  sx?: SxProps;
  slotProps?: { root?: BoxProps; content?: InsetProps };
  testId?: string;
};

export const Page = forwardRef<HTMLDivElement, PageProps>(
  ({ stack = true, cssVars, slotProps, sx, children, testId }, ref) => {
    return (
      <Box
        ref={ref}
        data-testid={testId}
        {...slotProps?.root}
        sx={mergeSx({ height: '100%' }, slotProps?.root?.sx)}
        style={mergeStyles(dsVars, slotProps?.root?.style, cssVars)}
        className={clsx(classNames.root, slotProps?.root?.className)}
      >
        <Inset
          {...slotProps?.content}
          className={clsx(classNames.content, slotProps?.content?.className)}
          sx={mergeSx(
            {
              height: '100%',
              position: 'relative',
              containerName: 'page',
              containerType: 'inline-size',
            },
            stack && {
              display: 'flex',
              flexDirection: 'column',
              gap: layoutVars.refs.sectionSpacing,
            },
            sx
          )}
          // TODO (Justas): Once all pages are migrated to use the new page, remove this line, and remove the padding from main layout
          px={false}
        >
          {children}
        </Inset>
      </Box>
    );
  }
);

Page.displayName = 'Page';
